interface EntryPointOptions {
  locationSearch?: string;
}

const getSearchParamEntryPoint = (locationSearch: string): string | null => {
  const params = new URLSearchParams(locationSearch);
  const entryPoint = params.get('entry-point');

  if (!entryPoint) {
    return null;
  }

  try {
    return decodeURIComponent(entryPoint);
  } catch {
    return entryPoint;
  }
};

const domainSourceRules: Record<string, RegExp> = {
  'FAA Landing Page': /\/real-estate-agents\/?(?:[?#]|$)/,
  'FAA Search Results':
    /\/real-estate-(agents|agencies)\/(?:search-results|[a-z0-9-]+-[a-z]{2,3}-\d{4})/,
  'Agent Profile': /\/real-estate-agent\/[a-z0-9-]+-\d+/,
  'Agency Profile': /\/real-estate-agency\/[a-z0-9-]+-\d+/,
  'HPG Landing Page': /\/property-profile\/?(?:[?#]|$)/,
  'Property Profile': /\/property-profile\//,
  'Street Profile': /\/street-profile\//,
  'Suburb Profile': /\/suburb-profile\//,
  'School Catchment': /\/school-catchment\//,
  'Auction Results': /\/auction-results/,
  DFO: /\/my-properties\//,
  'Sell Landing Page': /\/sell\/?(?:[?#]|$)/,
  'Content - News': /\/news\//,
  'Content - Advice': /\/advice\//,
  'Content - Living': /\/living\//,
  'Content - Research': /\/research\//,
  'User Shortlist': /\/user\/shortlist/,
  'Homepage - New Homes': /\/new-homes\/?(?:[?#]|$)/,
  'Homepage - Rural': /\/rural\/?(?:[?#]|$)/,
  'Homepage - House and Land': /\/house-and-land\/?(?:[?#]|$)/,
  'Homepage - Retirement': /\/retirement\/?(?:[?#]|$)/,
  'Homepage - Rent': /[?&]mode=rent/,
  'Homepage - Sold': /[?&]mode=sold/,
  'Listing - Buy': /\/sale\/[^/]+-\d{6,}/,
  'Listing - Rent': /\/rent\/[^/]+-\d{6,}/,
  'Listing - Sold': /\/sold-listings\/[^/]+-\d{6,}/,
  'Search Results - Buy': /\/sale\//,
  'Search Results - Rent': /\/rent\//,
  'Search Results - Sold': /\/sold-listings\//,
  'Homepage - Buy': /^\/?(?:[?#].*)?$/i,
};

const isInternalHost = (hostname: string, domain: string): boolean =>
  hostname === domain || hostname.endsWith(`.${domain}`);

const isInternalDomainUrl = (hostname: string): boolean =>
  isInternalHost(hostname, 'localhost') ||
  isInternalHost(hostname, 'domain.com.au');

const categorizeFromUrl = (url: string): string | null => {
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.toLowerCase();

    if (isInternalDomainUrl(hostname)) {
      const urlToMatch = parsedUrl.pathname + parsedUrl.search;
      const sources = Object.keys(domainSourceRules);
      const matchedSource = sources.find((category) =>
        urlToMatch.match(domainSourceRules[category]),
      );

      if (matchedSource) {
        return matchedSource;
      }
    }
  } catch {
    return null;
  }

  return null;
};

const GENERIC_SOURCES = [
  'Content',
  'Search Results',
  'Homepage',
  'Listing',
] as const;

const categorizeSource = (source: string): string => {
  if (source.startsWith('http')) {
    const categorized = categorizeFromUrl(source);
    if (categorized) {
      return categorized;
    }

    // If no match, check if internal or external
    try {
      const url = new URL(source);
      const hostname = url.hostname.toLowerCase();
      const isInternal = isInternalDomainUrl(hostname);

      return isInternal ? source : url.hostname;
    } catch {
      return source;
    }
  }

  // If source is a known generic value, try to expand using document.referrer
  if (
    GENERIC_SOURCES.includes(source as (typeof GENERIC_SOURCES)[number]) &&
    typeof window !== 'undefined' &&
    document.referrer
  ) {
    const categorized = categorizeFromUrl(document.referrer);
    if (categorized) {
      return categorized;
    }
  }

  return source;
};

const getEntryPoint = (options: EntryPointOptions = {}): string | null => {
  const isBrowser = typeof window !== 'undefined';

  const locationSearch =
    options.locationSearch ?? (isBrowser ? window.location.search : '');
  const queryEntryPoint = getSearchParamEntryPoint(locationSearch);

  if (queryEntryPoint) {
    return queryEntryPoint;
  }

  if (isBrowser) {
    const digitalDataSource = (
      window as { digitalData?: { page?: { pageInfo?: { source?: string } } } }
    )?.digitalData?.page?.pageInfo?.source;

    if (digitalDataSource) {
      // Handle "Unknown" source first
      if (digitalDataSource === 'Unknown') {
        // If no referrer exists, it's a direct visit (first page load)
        return document.referrer ? null : 'Direct';
      }

      // Handle URL sources
      if (digitalDataSource.startsWith('http')) {
        // Exception: Exclude self-referral for FAA search results navigation
        const faaSearchPattern = domainSourceRules['FAA Search Results'];
        const sourceIsFaaSearch = faaSearchPattern.test(digitalDataSource);
        const currentIsFaaSearch = faaSearchPattern.test(window.location.href);

        if (sourceIsFaaSearch && currentIsFaaSearch) {
          return null;
        }

        // Categorize the URL source
        return categorizeSource(digitalDataSource);
      }

      // Source is already categorized (e.g., "Content", "Search Results"), try to expand it
      return categorizeSource(digitalDataSource);
    }
  }

  return null;
};

export default getEntryPoint;
